import { useLocation } from '../../../features/locations/use-location'
import { useNetworkOverview } from '../../../features/locations/use-network-overview'
import { useIdOrCode } from '../../use-id-or-code'
import { AppLink, Badge, Card, Crumb, Facts, LinkButton, mono, Notice, PageHeader, SkeletonCards, Stat } from './ui'

export function PorcelainLocationDetail() {
  const idOrCode = useIdOrCode()
  const { data: loc, isLoading, error } = useLocation(idOrCode)
  const net = useNetworkOverview(idOrCode)

  if (isLoading) return <SkeletonCards rows={3} />
  if (error)
    return (
      <Notice
        tone="error"
        title="We couldn't load this location"
        body="Check your connection and try again in a moment."
      />
    )
  if (!loc) return <Notice title="Location not found" body={`Nothing matches "${idOrCode}".`} />

  const profile = loc.networkProfiles?.[0] // Drizzle relations are arrays
  const address = [loc.addressLine1, [loc.addressCity, loc.addressState].filter(Boolean).join(', '), loc.addressZipCode]
    .filter(Boolean)
    .join(' · ')

  return (
    <section>
      <PageHeader
        eyebrow={<Crumb idOrCode={idOrCode} trail="detail" />}
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span>{loc.name}</span>
            <Badge label={loc.status ? 'Active' : 'Inactive'} />
          </span>
        }
        subtitle={
          <>
            <span style={mono}>{loc.aptosStoreCode ?? loc.code}</span>
            {address ? ` · ${address}` : ''}
          </>
        }
        right={<LinkButton to={`/locations/${encodeURIComponent(idOrCode)}/network`}>View network</LinkButton>}
      />

      <div className="mt-8 grid gap-5 lg:grid-cols-2">
        <Card title="Overview">
          <Facts
            items={[
              ['Banner', loc.banner?.toUpperCase()],
              ['Region', loc.region],
              ['District', loc.district],
              ['Type', loc.locationTypeLabel],
              [
                'Store code',
                <span key="c" style={mono}>
                  {loc.aptosStoreCode ?? loc.code}
                </span>,
              ],
              ['Terminals', loc.terminals ? String(loc.terminals.length) : null],
            ]}
          />
        </Card>

        <Card title="Network profile">
          {profile ? (
            <Facts
              items={[
                ['Lifecycle', profile.lifecycleState ? <Badge key="l" label={profile.lifecycleState} /> : null],
                ['Edge', profile.edgeVendor],
                ['Switching', profile.switchVendor],
                ['Cloud gateway', profile.cgVendor],
                [
                  'Primary gateway',
                  profile.ipPrimaryGw ? (
                    <span key="g" style={mono}>
                      {String(profile.ipPrimaryGw)}
                    </span>
                  ) : null,
                ],
                [
                  'Primary CIDR',
                  profile.ipPrimaryCidr ? (
                    <span key="p" style={mono}>
                      {String(profile.ipPrimaryCidr)}
                    </span>
                  ) : null,
                ],
              ]}
            />
          ) : (
            <p className="px-5 py-8 text-center text-[13px] text-[var(--color-muted)]">
              No network profile has been set up for this store yet.
            </p>
          )}
        </Card>
      </div>

      <div className="mt-5">
        <Card
          title="Device status"
          aside={
            <AppLink
              to={`/locations/${encodeURIComponent(idOrCode)}/network`}
              className="text-[13px] text-[var(--color-accent)] hover:underline"
            >
              Open network
            </AppLink>
          }
        >
          {net.isLoading ? (
            <div className="grid grid-cols-2 gap-4 p-5 md:grid-cols-4" aria-hidden="true">
              {['a', 'b', 'c', 'd'].map((k) => (
                <div key={k} className="v1-skeleton h-16" />
              ))}
            </div>
          ) : net.data ? (
            <div className="grid grid-cols-2 gap-4 p-5 md:grid-cols-4">
              <Stat label="Devices" value={net.data.summary.total} />
              <Stat label="Up" value={net.data.summary.up} tone="ok" />
              <Stat label="Down" value={net.data.summary.down} tone={net.data.summary.down > 0 ? 'bad' : undefined} />
              <Stat label="Other" value={net.data.summary.other} />
            </div>
          ) : (
            <p className="px-5 py-8 text-center text-[13px] text-[var(--color-muted)]">
              {net.error ? 'Device status is unavailable right now.' : 'No device status reported for this store.'}
            </p>
          )}
        </Card>
      </div>
    </section>
  )
}
